import CloseIcon from '@material-design-icons/svg/filled/close.svg?react';
import { isLeft } from 'fp-ts/Either';
import { useCallback, useEffect, useState } from 'react';

import { cn } from '../../../lib/utils';
import type {
  LoadDappReviews,
  ReviewSort,
  SpotlightFeaturedReview,
  StoreReview,
} from '../types';
import {
  FEATURED_REVIEW_MAX_LENGTH,
  formatDate,
  reviewToFeaturedReview,
  reviewerLabel,
} from '../utils';
import { RatingStars } from './RatingStars';

interface ReviewPickerDialogProps {
  dappId: string;
  appName: string;
  loadReviews: LoadDappReviews;
  onClose: () => void;
  onSelect: (review: SpotlightFeaturedReview) => void;
}

const SORTS: { key: ReviewSort; label: string }[] = [
  { key: 'newest', label: 'Newest' },
  { key: 'highest', label: 'Highest rated' },
];

function getReviewTimestamp(review: StoreReview) {
  const timestamp = Date.parse(review.createdAt);
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function sortLoadedReviews(reviews: StoreReview[], sortBy: ReviewSort) {
  if (sortBy !== 'highest') {
    return reviews;
  }

  return [...reviews].sort((left, right) => {
    const ratingDelta = right.rating - left.rating;
    if (ratingDelta !== 0) {
      return ratingDelta;
    }
    return getReviewTimestamp(right) - getReviewTimestamp(left);
  });
}

export function ReviewPickerDialog({
  dappId,
  appName,
  loadReviews,
  onClose,
  onSelect,
}: ReviewPickerDialogProps) {
  const [sortBy, setSortBy] = useState<ReviewSort>('newest');
  const [reviews, setReviews] = useState<StoreReview[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [storeListingFound, setStoreListingFound] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (targetPage: number, append: boolean) => {
      setIsLoading(true);
      setError(null);
      const result = await loadReviews(dappId, {
        page: targetPage,
        sortBy: 'newest',
      });
      setIsLoading(false);
      if (isLeft(result)) {
        setError(result.left.message);
        return;
      }
      const data = result.right;
      setStoreListingFound(data.storeListingFound);
      setTotalPages(data.pagination.totalPages);
      setPage(data.pagination.currentPage);
      setReviews((current) =>
        append ? [...current, ...data.reviews] : data.reviews,
      );
    },
    [dappId, loadReviews],
  );

  useEffect(() => {
    void load(1, false);
  }, [load]);

  const withText = sortLoadedReviews(
    reviews.filter((review) => (review.review ?? '').trim().length > 0),
    sortBy,
  );
  const hasMore = page < totalPages;

  const handlePick = (review: StoreReview) => {
    const mapped = reviewToFeaturedReview(review);
    if (mapped) {
      onSelect(mapped);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-2xl border border-neutral-800 bg-neutral-900 shadow-2xl">
        <header className="flex items-center justify-between gap-3 border-b border-neutral-800 px-5 py-4">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-white">
              Feature a review
            </h3>
            <p className="truncate text-xs text-neutral-500">{appName}</p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex size-8 items-center justify-center rounded-lg text-neutral-400 hover:bg-neutral-800 hover:text-white"
          >
            <CloseIcon className="size-4 fill-current" />
          </button>
        </header>

        <div className="flex items-center gap-1 border-b border-neutral-800 px-5 py-2">
          {SORTS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setSortBy(key)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                sortBy === key
                  ? 'bg-neutral-800 text-white'
                  : 'text-neutral-500 hover:text-neutral-300',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {error ? (
            <div className="px-2 py-8 text-center text-sm text-red-300">
              {error}
            </div>
          ) : !storeListingFound ? (
            <div className="px-2 py-8 text-center text-sm text-neutral-500">
              No store listing found for this app.
            </div>
          ) : withText.length === 0 && !isLoading ? (
            <div className="px-2 py-8 text-center text-sm text-neutral-500">
              No written reviews to feature yet.
            </div>
          ) : (
            <ul className="space-y-2">
              {withText.map((review) => {
                const text = (review.review ?? '').trim();
                const tooLong = text.length > FEATURED_REVIEW_MAX_LENGTH;
                return (
                  <li key={review.id}>
                    <button
                      type="button"
                      disabled={tooLong}
                      onClick={() => handlePick(review)}
                      className={cn(
                        'w-full rounded-xl border border-neutral-800 bg-neutral-950/40 p-3 text-left transition-colors',
                        tooLong
                          ? 'cursor-not-allowed opacity-50'
                          : 'hover:border-teal-500/40 hover:bg-neutral-900/60',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <RatingStars rating={review.rating} />
                        <span className="truncate text-xs text-neutral-400">
                          {reviewerLabel(review)}
                        </span>
                        <span className="ml-auto shrink-0 text-[11px] text-neutral-600">
                          {formatDate(review.createdAt)}
                        </span>
                      </div>
                      <p className="mt-1.5 line-clamp-4 text-sm text-neutral-200">
                        {text}
                      </p>
                      {tooLong && (
                        <p className="mt-1 text-[11px] text-amber-400">
                          Too long to feature (over {FEATURED_REVIEW_MAX_LENGTH}{' '}
                          characters)
                        </p>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <div className="size-5 animate-spin rounded-full border-2 border-neutral-700 border-t-neutral-300" />
            </div>
          )}
        </div>

        {hasMore && !isLoading && storeListingFound && !error && (
          <div className="border-t border-neutral-800 px-5 py-3">
            <button
              type="button"
              onClick={() => void load(page + 1, true)}
              className="w-full rounded-lg border border-neutral-800 py-2 text-xs font-medium text-neutral-300 hover:bg-neutral-800/50"
            >
              Load more reviews
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
